import { useTimeoutFn } from '@vueuse/core'

import { getTooltipPropsFromElement, isOverFlow } from '@/directive/v-tooltip/utils'

import type { ElTooltipProps, VTooltipOption } from '@/directive/v-tooltip/types'
import type { Ref } from 'vue'

/**
 * 获得一个受控的 ref
 * 赋非空值会立即赋值
 * 赋空值会延迟特定时间后才实际赋值
 * 用来解决 virtual-ref 赋空值后, tooltip 元素没有过渡动画直接消失的问题
 */
export function useLazyElem(): Ref<HTMLElement | null> {
  const vTooltipElem = ref<HTMLElement | null>(null)
  const lazyVTooltipElem = computed<HTMLElement | null>({
    get: () => unref(vTooltipElem),
    set: val => {
      if (val) {
        vTooltipElem.value = val
        stop()
      } else if (unref(vTooltipElem) && !unref(isPending)) {
        start()
      }
    }
  })
  const { start, stop, isPending } = useTimeoutFn(
    () => {
      vTooltipElem.value = null
    },
    500,
    { immediate: false }
  )

  return lazyVTooltipElem
}

/**
 * 获取元素可用的 tooltipOption
 */
export function useTooltipOption(elemRef: Ref<HTMLElement | null>) {
  const tooltipOption = ref<ElTooltipProps | null>(null)

  // tooltip 目标元素变化时, 获取 tooltip option
  watchEffect(() => {
    const elem = unref(elemRef)
    let option: VTooltipOption | null = elem && getTooltipPropsFromElement(elem)
    // 特殊 option 处理
    if (elem && option) {
      // 如果设置了仅溢出时显示 tooltip 但元素未溢出, 则不显示 tooltip
      if (!option.always && !isOverFlow(elem)) {
        option = null
      }
    }

    tooltipOption.value = option
  })

  const hasOption = computed(() => !!unref(tooltipOption)?.content)

  return {
    tooltipOption,
    hasOption
  }
}
